import React from 'react';
import type { LinkProps } from '@reach/router';
import styled from 'styled-components';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import breakpoints from '../breakpoints';

interface GatsbyLinkProps<TState> extends LinkProps<TState> {
  /** A class to apply when this Link is active */
  activeClassName?: string;
  /** Inline styles for when this Link is active */
  activeStyle?: object;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Class the link as highlighted if there is a partial match via a the `to` being prefixed to the current url */
  partiallyActive?: boolean;
  /** Used to declare that this link replaces the current URL in history with the target */
  replace?: boolean;
  /** Used to pass state data to the linked page.
   * The linked page will have a `location` prop containing a nested `state` object structure containing the passed data.
   */
  state?: TState;
  /** The URL you want to link to */
  to: string;
}

interface StyledButtonProps {
  $importance?: 'primary' | 'secondary' | 'tertiary';
  $fullWidth?: boolean;
  $mobileFullWidth?: boolean;
  disabled?: boolean;
  name?: string;
}

type ButtonAsButton = StyledButtonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement>;
type ButtonAsLink = StyledButtonProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
type ButtonAsGatsbyLink = StyledButtonProps &
  GatsbyLinkProps<Record<string, never>> & { to: string };
type CustomButtonProps = {
  importance?: string;
  fullWidth?: boolean;
  mobileFullWidth?: boolean;
  children?: React.ReactNode;
};

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsGatsbyLink;

// Filter out the props that shouldn't be passed to the DOM
const StyledButton = styled.button<StyledButtonProps>`
  display: inline-block;
  padding: ${({ theme }) => theme.button.padding};
  cursor: pointer;
  text-decoration: none;
  background: ${({ theme, $importance }) =>
    theme.button[$importance].background};
  border-width: ${({ theme }) => theme.button.borderWidth};
  border-style: ${({ theme }) => theme.button.borderStyle};
  border-color: ${({ theme, $importance }) =>
    theme.button[$importance].borderColor};
  border-radius: ${({ theme }) => theme.button.borderRadius};
  box-shadow: ${({ theme }) => theme.button.boxShadow};
  width: ${({ $mobileFullWidth, $fullWidth }) =>
    $mobileFullWidth || $fullWidth ? '100%' : 'auto'};

  @media screen and (min-width: ${breakpoints.md}) {
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  }

  &:not(.disabled):hover,
  &:not(.disabled):focus-visible {
    text-decoration: none;
    background: ${({ theme, $importance }) =>
      theme.button[$importance].backgroundHover};
    border-color: ${({ theme, $importance }) =>
      theme.button[$importance].borderColorHover};
    border-style: ${({ theme }) => theme.button.borderStyleHover};
  }

  &:not(.disabled):active {
    background: ${({ theme, $importance }) =>
      theme.button[$importance].backgroundActive};
    border-color: ${({ theme, $importance }) =>
      theme.button[$importance].borderColorActive};
  }

  &.disabled {
    opacity: 0.75;
    cursor: not-allowed;
    background: ${({ theme, $importance }) =>
      theme.button[$importance].backgroundDisabled};
  }

  & > span {
    display: block;
    margin: 0;
    padding: ${({ theme }) => theme.button.paddingInner};
    text-align: center;
    font-weight: bold;
    font-size: 1.1875rem;
    line-height: 1.15;
    text-transform: ${({ theme }) => theme.button.textTransform};
    color: ${({ theme, $importance }) => theme.button[$importance].textColor};
    border-width: ${({ theme }) => theme.button.borderWidthInner};
    border-style: ${({ theme }) => theme.button.borderStyleInner};
    border-color: ${({ theme, $importance }) =>
      theme.button[$importance].borderColorInner};
    border-radius: ${({ theme }) => theme.button.borderRadiusInner};
  }

  &:not(.disabled):hover > span,
  &:not(.disabled):focus-visible > span {
    border-color: ${({ theme, $importance }) =>
      theme.button[$importance].borderColorInnerHover};
    border-style: ${({ theme }) => theme.button.borderStyleInnerHover};
    color: ${({ theme, $importance }) =>
      theme.button[$importance].textColorHover};
  }

  &:not(.disabled):active > span {
    border-color: ${({ theme, $importance }) =>
      theme.button[$importance].borderColorInnerActive};
    color: ${({ theme, $importance }) =>
      theme.button[$importance].textColorActive};
  }
`;

const Button: React.FC<ButtonProps & CustomButtonProps> = ({
  importance = 'primary',
  fullWidth = false,
  mobileFullWidth = false,
  ...options
}) => {
  const buttonClass = `${options?.className || ''}${
    options.disabled ? ' disabled' : ''
  }${importance ? ` btn-${importance}` : ''}`;

  let Component: React.ElementType = 'button';
  if ('to' in options) {
    Component = LocalizedLink;
  } else if ('href' in options) {
    Component = 'a';
  }

  return (
    <StyledButton
      $importance={importance}
      $fullWidth={fullWidth}
      $mobileFullWidth={mobileFullWidth}
      as={Component}
      className={buttonClass}
      {...options}
    >
      <span>{options.children}</span>
    </StyledButton>
  );
};

export default Button;
